import apiClient from './apiClient';

/**
 * Country interface
 */
export interface Country {
  Country_ID: string;
  name: string;
  abbrev: string;
  city_count?: number;
}

/**
 * City interface
 */
export interface City {
  City_ID: string;
  name: string;
  Country_ID: string;
}

/**
 * Location interface
 */
export interface Location {
  Location_ID: string;
  location_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Location Create interface
 */
export interface LocationCreate {
  location_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
}

/**
 * Location Update interface
 */
export interface LocationUpdate {
  location_name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  is_active?: boolean;
}

/**
 * Location API functions
 */
export const locationApi = {
  /**
   * Get all countries
   */
  getAllCountries: async (): Promise<Country[]> => {
    const response = await apiClient.get<Country[]>('/api/v2/web/countries');
    return response.data;
  },

  /**
   * Get a single country by ID
   */
  getCountryById: async (countryId: string): Promise<Country> => {
    const response = await apiClient.get<Country>(`/api/v2/web/countries/${countryId}`);
    return response.data;
  },

  /**
   * Create a new country
   */
  createCountry: async (countryData: { name: string; abbrev: string }): Promise<Country> => {
    const response = await apiClient.post<Country>('/api/v2/web/countries', countryData);
    return response.data;
  },

  /**
   * Update a country
   */
  updateCountry: async (countryId: string, countryData: { name: string; abbrev: string }): Promise<Country> => {
    const response = await apiClient.put<Country>(`/api/v2/web/countries/${countryId}`, countryData);
    return response.data;
  },

  /**
   * Delete a country
   */
  deleteCountry: async (countryId: string) => {
    const response = await apiClient.delete(`/api/v2/web/countries/${countryId}`);
    return response.data;
  },

  /**
   * Get all cities
   */
  getAllCities: async (countryId?: string): Promise<City[]> => {
    const url = countryId 
      ? `/api/v2/web/cities?country_id=${countryId}` 
      : '/api/v2/web/cities';
    const response = await apiClient.get<City[]>(url);
    return response.data;
  },

  /**
   * Get a single city by ID
   */
  getCityById: async (cityId: string): Promise<City> => {
    const response = await apiClient.get<City>(`/api/v2/web/cities/${cityId}`);
    return response.data;
  },

  /**
   * Create a new city
   */
  createCity: async (cityData: { name: string; Country_ID: string }): Promise<City> => {
    const response = await apiClient.post<City>('/api/v2/web/cities', cityData);
    return response.data;
  },

  /**
   * Update a city
   */
  updateCity: async (cityId: string, cityData: { name: string; Country_ID: string }): Promise<City> => {
    const response = await apiClient.put<City>(`/api/v2/web/cities/${cityId}`, cityData);
    return response.data;
  },

  /**
   * Delete a city
   */
  deleteCity: async (cityId: string) => {
    const response = await apiClient.delete(`/api/v2/web/cities/${cityId}`);
    return response.data;
  },

  // ========================================
  // SAVED LOCATIONS API
  // ========================================

  /**
   * Get all saved locations
   */
  getAllLocations: async (includeInactive: boolean = false): Promise<Location[]> => {
    const url = includeInactive 
      ? '/api/v2/web/locations?include_inactive=true' 
      : '/api/v2/web/locations';
    const response = await apiClient.get<Location[]>(url);
    return response.data;
  },

  /**
   * Get a single location by ID
   */
  getLocationById: async (locationId: string): Promise<Location> => {
    const response = await apiClient.get<Location>(`/api/v2/web/locations/${locationId}`);
    return response.data;
  },

  /**
   * Create a new saved location
   */
  createLocation: async (locationData: LocationCreate): Promise<Location> => {
    const response = await apiClient.post<Location>('/api/v2/web/locations', locationData);
    return response.data;
  },

  /**
   * Update a saved location
   */
  updateLocation: async (locationId: string, locationData: LocationUpdate): Promise<Location> => {
    const response = await apiClient.put<Location>(`/api/v2/web/locations/${locationId}`, locationData);
    return response.data;
  },

  /**
   * Delete a saved location
   */
  deleteLocation: async (locationId: string) => {
    const response = await apiClient.delete(`/api/v2/web/locations/${locationId}`);
    return response.data;
  },

  /**
   * Activate a deactivated location
   */
  activateLocation: async (locationId: string): Promise<Location> => {
    const response = await apiClient.put<Location>(`/api/v2/web/locations/${locationId}/activate`);
    return response.data;
  }
};
